import React, { useState } from "react";
import { Container, Box, Typography, Tab, Tabs, Paper, Alert } from "@mui/material";

// Context
import { ThemeProvider } from "./context/ThemeContext";

// Components
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import FileUpload from "./components/Encryption/FileUpload";
import EncryptionForm from "./components/Encryption/EncryptionForm";
import EncryptedOutput from "./components/Encryption/EncryptedOutput";
import DecryptionForm from "./components/Decryption/DecryptionForm";
import DecryptedOutput from "./components/Decryption/DecryptedOutput";
import VideoPlayer from "./components/Common/VideoPlayer";
import InstructionsDialog from "./components/Common/InstructionsDialog";
import HelpBanner from "./components/Common/HelpBanner";

// Utils
import { encryptFile, decryptFile, generateFilename } from "./utils/encryption";
import { readFileAsArrayBuffer } from "./utils/fileHandling";
import blobStorageService from "./utils/blobStorage";

// Custom TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function AppContent() {
  // State
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [encryptKey, setEncryptKey] = useState("");
  const [encryptedData, setEncryptedData] = useState("");
  const [encryptedDataPasted, setEncryptedDataPasted] = useState("");
  const [decryptedData, setDecryptedData] = useState(null);
  const [fileFormat, setFileFormat] = useState("PNG");
  const [ipfsHash, setIpfsHash] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  // Handle file upload
  const handleFileUpload = (selectedFile) => {
    setFile(selectedFile);
    setFileName(selectedFile.name);
    setEncryptedData(""); // Clear previous encrypted data
    setIpfsHash(""); // Clear previous storage URL
    setErrorMessage(""); // Clear any error messages
  };

  // Handle encryption
  const handleEncrypt = async () => {
    if (!file || !encryptKey) {
      setErrorMessage("Please provide both a file and an encryption key");
      return;
    }

    setIsEncrypting(true);
    setErrorMessage("");

    try {
      // Read file as ArrayBuffer
      const fileData = await readFileAsArrayBuffer(file);
      
      // Encrypt the file
      const result = await encryptFile(fileData, encryptKey);
      
      if (result.success) {
        setEncryptedData(result.data);
      } else {
        setErrorMessage(result.error);
      }
    } catch (error) {
      console.error("Encryption error:", error);
      setErrorMessage(`Encryption failed: ${error.message}`);
    } finally {
      setIsEncrypting(false);
    }
  };

  // Handle decryption
  const handleDecrypt = async () => {
    if (!encryptedDataPasted || !encryptKey) {
      setErrorMessage("Please provide both encrypted data and a decryption key");
      return;
    }

    setIsDecrypting(true);
    setErrorMessage("");

    try {
      // Decrypt the data
      const result = await decryptFile(encryptedDataPasted, encryptKey, fileFormat);
      
      if (result.success) {
        setDecryptedData(result.data);
      } else {
        setErrorMessage(result.error);
      }
    } catch (error) {
      console.error("Decryption error:", error);
      setErrorMessage(`Decryption failed: ${error.message}`);
    } finally {
      setIsDecrypting(false);
    }
  };

  // Handle download encrypted data
  const handleDownloadEncryptedData = () => {
    if (!encryptedData) return;

    const filename = generateFilename("encrypted_data", "txt");
    
    // Create a Blob with the encrypted data
    const blob = new Blob([encryptedData], { type: "text/plain" });
    
    // Create a temporary link and trigger download
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  };

  // Handle download decrypted data
  const handleDownloadDecryptedData = () => {
    if (!decryptedData) return;

    const filename = generateFilename("decrypted_file", fileFormat.toLowerCase());
    
    // Create a temporary link and trigger download
    const link = document.createElement("a");
    link.href = decryptedData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
  };

  // Handle upload to Vercel Blob Storage
  const handleUploadToBlobStorage = async () => {
    if (!encryptedData) {
      setErrorMessage("No encrypted data to upload");
      return;
    }

    setIsUploading(true);
    setErrorMessage("");

    try {
      const result = await blobStorageService.uploadEncryptedData(
        encryptedData,
        fileName,
        fileFormat
      );

      if (result.success) {
        setIpfsHash(result.url); // Using same state variable for blob URL
      } else {
        setErrorMessage(result.error);
      }
    } catch (error) {
      console.error("Blob storage upload error:", error);
      setErrorMessage(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleOpenInstructions = () => {
    setInstructionsOpen(true);
  };
  
  const handleCloseInstructions = () => {
    setInstructionsOpen(false);
  };

  return (
    <>
      <Header onOpenInstructions={handleOpenInstructions} />
      <InstructionsDialog open={instructionsOpen} onClose={handleCloseInstructions} />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Hero section */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mb: 4, 
            textAlign: 'center',
            bgcolor: 'primary.main',
            color: 'white'
          }}
        >
          <Typography variant="h3" gutterBottom>
            Image Encryption for Legal Claims
          </Typography>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Secure your sensitive images with client-side encryption
          </Typography>
          <Typography variant="body1">
            Encrypt your images before uploading them to Vercel Blob Storage, ensuring privacy and security
            for confidential legal documents and evidence.
          </Typography>
        </Paper>

        {/* Error message display */}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        {/* Main tabs */}
        <Paper elevation={2} sx={{ mb: 4 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            aria-label="app functions"
          >
            <Tab label="Encrypt" id="tab-0" aria-controls="tabpanel-0" />
            <Tab label="Decrypt" id="tab-1" aria-controls="tabpanel-1" />
          </Tabs>

          {/* Encrypt Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box>
              {/* Help Banner */}
              <HelpBanner mode="encrypt" onOpenFullInstructions={handleOpenInstructions} />
              
              {/* File Upload */}
              <FileUpload 
                onFileUpload={handleFileUpload} 
                fileFormat={fileFormat} 
                setFileFormat={setFileFormat}
                fileName={fileName}
              />
              
              {/* Encryption Form */}
              <EncryptionForm 
                encryptKey={encryptKey} 
                setEncryptKey={setEncryptKey} 
                onEncrypt={handleEncrypt}
                isEncrypting={isEncrypting}
                hasFile={!!file}
              />
              
              {/* Encrypted Output */}
              {encryptedData && (
                <EncryptedOutput
                  encryptedData={encryptedData}
                  onDownload={handleDownloadEncryptedData}
                  onUploadToBlobStorage={handleUploadToBlobStorage}
                  ipfsHash={ipfsHash}
                  isUploading={isUploading}
                />
              )}
            </Box>
          </TabPanel>

          {/* Decrypt Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box>
              {/* Help Banner */}
              <HelpBanner mode="decrypt" onOpenFullInstructions={handleOpenInstructions} />
              
              {/* Decryption Form */}
              <DecryptionForm 
                encryptKey={encryptKey}
                setEncryptKey={setEncryptKey}
                encryptedDataPasted={encryptedDataPasted}
                setEncryptedDataPasted={setEncryptedDataPasted}
                fileFormat={fileFormat}
                setFileFormat={setFileFormat}
                onDecrypt={handleDecrypt}
                isDecrypting={isDecrypting}
              />
              
              {/* Decrypted Output */}
              {decryptedData && (
                <DecryptedOutput 
                  decryptedData={decryptedData} 
                  fileFormat={fileFormat}
                  onDownload={handleDownloadDecryptedData}
                />
              )}
            </Box>
          </TabPanel>
        </Paper>

        {/* Video player */}
        <VideoPlayer
          title="How to Encrypt Your Images"
          url="/video/0_2025-pt5-upload-encrypt-evidencer-video-intro.mp4"
          posterUrl="https://zpl0eg1fzvvynkgt.public.blob.vercel-storage.com/w3le-200w-jWDyOUJ8HoreMXISAEGro21IZhF2hq.png"
        />
      </Container>
      <Footer />
    </>
  );
}

// Main App component with ThemeProvider wrapper
function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;